import { useState } from "react";
import { Prompt } from "../components/Audio";
import { CheckIcon, CrossIcon } from "../components/Icons";
import ItemCard from "../components/ItemCard";
import Picture from "../components/Picture";
import ui from "../components/ui.module.css";
import useSentBack from "../hooks/useSentBack";

const HINT_AFTER = 2;

// Yes and No differ by shape as well as by word, so neither colour nor
// reading is needed to tell them apart.
const MARKS = {
  yes: <CheckIcon className={ui.markYes} size={88} />,
  no: <CrossIcon className={ui.markNo} size={88} />,
};

// Look at the picture, then choose. The choice is handed in whether or not it
// matches the expected answer; the gateway records it and its policy decides
// whether the learner moves on (Handoff 007 §3). When a step must be got
// right, a choice sent back is shaken, and after two the right card is hinted
// if (and only if) feedback policy has already permitted the answer here.
const ChoiceStep = ({ step, saved, verdict, onDone, saving, readOnly = false }) => {
  // Only a read-only review starts from the saved choice; real work always
  // starts open, even after an answer that must be tried again.
  const [picked, setPicked] = useState(readOnly ? saved?.value ?? null : null);
  const [wrong, setWrong] = useState(null);
  const [misses, setMisses] = useState(0);

  // A response sent back to try again: unlock and show which card it was.
  useSentBack(verdict, () => {
    setWrong(picked);
    setPicked(null);
    setMisses((n) => n + 1);
  });

  const choose = (option) => {
    if (picked || saving) return;
    setWrong(null);
    setPicked(option.value);
    onDone({ value: option.value });
  };

  const hint = misses >= HINT_AFTER && !picked ? step.correct : null;
  const right = verdict?.kind === "right";

  return (
    <>
      {step.stimulus && (
        <img
          className={ui.stimulus}
          src={step.stimulus.src}
          alt={step.stimulus.alt}
        />
      )}
      <Prompt step={step} />
      <div className={ui.choices}>
        {step.options.map((option) => (
          <ItemCard
            // Re-mounting on each miss replays the shake.
            key={option.value === wrong ? `${option.value}-${misses}` : option.value}
            item={option}
            className={[
              ui.choice,
              picked === option.value && (right ? ui.choiceRight : ui.chosen),
              wrong === option.value && ui.shake,
              hint === option.value && ui.choiceHint,
            ]
              .filter(Boolean)
              .join(" ")}
            onClick={() => choose(option)}
            aria-pressed={picked === option.value}
            disabled={saving && picked !== option.value}
          >
            {!option.asset && !option.emoji && MARKS[option.value]}
            <Picture item={option} />
          </ItemCard>
        ))}
      </div>
    </>
  );
};

export default ChoiceStep;
